import { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AppContext";
import { AdminTitle } from "./AdminLayout";

const STATUS_LABEL = { draft: "Bozza", published: "Pubblicato", hidden: "Nascosto", archived: "Archiviato" };
const DAYS = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];

function useRefs(fields) {
  const [refs, setRefs] = useState({});
  useEffect(() => {
    [...new Set(fields.flatMap((f) => f.refs || (f.ref ? [f.ref] : [])))].forEach((r) =>
      api.get(`/admin/${r}`).then((res) => setRefs((s) => ({ ...s, [r]: res.data }))).catch(() => {}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return refs;
}

function MediaField({ value, onChange }) {
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file); fd.append("purpose", "content");
    try { onChange((await api.post("/admin/media", fd)).data.id); toast.success("Immagine caricata"); } catch (err) { toast.error(errorMessage(err)); }
  };
  return (
    <div className="flex items-center gap-4">
      {value && <img src={mediaUrl(value, "sm")} alt="" className="h-16 w-16 rounded object-cover" />}
      <input type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} data-testid="media-upload-input" />
      {value && <button type="button" className="text-xs text-red-700" onClick={() => onChange(null)}>Rimuovi</button>}
    </div>
  );
}

function FaqsField({ value = [], onChange }) {
  const upd = (i, k, v) => onChange(value.map((f, j) => (j === i ? { ...f, [k]: v } : f)));
  return (
    <div className="space-y-3">
      {value.map((f, i) => (
        <div key={i} className="grid gap-2 rounded-xl border border-[var(--line)] p-3">
          <input className="field" placeholder="Domanda" value={f.q} onChange={(e) => upd(i, "q", e.target.value)} />
          <textarea className="field" rows={2} placeholder="Risposta" value={f.a} onChange={(e) => upd(i, "a", e.target.value)} />
          <button type="button" className="w-fit text-xs text-red-700" onClick={() => onChange(value.filter((_, j) => j !== i))}>Rimuovi</button>
        </div>
      ))}
      <button type="button" className="text-sm text-[var(--petrol)]" onClick={() => onChange([...value, { q: "", a: "" }])} data-testid="faq-add">+ Aggiungi FAQ</button>
    </div>
  );
}

function HoursField({ value = {}, onChange }) {
  const setDay = (d, ivs) => onChange({ ...value, [d]: ivs });
  return (
    <div className="space-y-2">
      {DAYS.map((name, d) => {
        const ivs = value[String(d)] || [];
        return (
          <div key={d} className="flex flex-wrap items-center gap-2 text-sm">
            <span className="w-24">{name}</span>
            {ivs.map((iv, i) => (
              <span key={i} className="flex items-center gap-1 rounded-lg bg-[var(--ivory)] px-2 py-1">
                <input type="time" step={300} value={iv.start} onChange={(e) => setDay(String(d), ivs.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))} aria-label={`${name} inizio`} />–
                <input type="time" step={300} value={iv.end} onChange={(e) => setDay(String(d), ivs.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))} aria-label={`${name} fine`} />
                <button type="button" aria-label="Rimuovi fascia" onClick={() => setDay(String(d), ivs.filter((_, j) => j !== i))}><Trash2 size={14} /></button>
              </span>
            ))}
            <button type="button" className="text-xs text-[var(--petrol)]" onClick={() => setDay(String(d), [...ivs, { start: "09:00", end: "13:00" }])} data-testid={`hours-add-${d}`}>+ fascia</button>
            {ivs.length === 0 && <span className="text-xs text-[var(--muted-ink)]">chiuso</span>}
          </div>
        );
      })}
      <p className="text-xs text-[var(--muted-ink)]">Le pause si ottengono con più fasce nello stesso giorno (es. 09–13 e 14–19).</p>
    </div>
  );
}

function Field({ f, value, onChange, refs }) {
  const common = { className: "field mt-1", value: value ?? "", onChange: (e) => onChange(e.target.value), "data-testid": `field-${f.name}` };
  switch (f.type) {
    case "textarea": return <textarea rows={3} {...common} />;
    case "html": return <><textarea rows={10} {...common} className="field mt-1 font-mono text-xs" /><span className="text-xs text-[var(--muted-ink)]">HTML semplice consentito: p, h2, h3, ul, li, strong, em, a. Il resto viene rimosso dal server.</span></>;
    case "number": return <input type="number" {...common} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} />;
    case "money": return <input type="number" step="0.01" min="0" {...common} value={value == null ? "" : value / 100} onChange={(e) => onChange(e.target.value === "" ? null : Math.round(Number(e.target.value) * 100))} />;
    case "checkbox": return <input type="checkbox" className="ml-2" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={`field-${f.name}`} />;
    case "select": {
      const keys = f.refs || (f.ref ? [f.ref] : null);
      const opts = keys ? keys.flatMap((k) => (refs[k] || []).map((r) => [r.id, r.name || r.title])) : f.options;
      return <select {...common} onChange={(e) => onChange(e.target.value || null)}>{keys && <option value="">—</option>}{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>;
    }
    case "multi": return (
      <div className="mt-1 flex flex-wrap gap-2" data-testid={`field-${f.name}`}>{(refs[f.ref] || []).map((r) => (
        <label key={r.id} className="flex items-center gap-1 rounded-full border border-[var(--line)] px-3 py-1 text-xs"><input type="checkbox" checked={(value || []).includes(r.id)} onChange={(e) => onChange(e.target.checked ? [...(value || []), r.id] : (value || []).filter((x) => x !== r.id))} />{r.name || r.title}</label>
      ))}<span className="text-xs text-[var(--muted-ink)]">{f.hint}</span></div>
    );
    case "media": return <div className="mt-1"><MediaField value={value} onChange={onChange} /></div>;
    case "faqs": return <div className="mt-1"><FaqsField value={value} onChange={onChange} /></div>;
    case "hours": return <div className="mt-1"><HoursField value={value} onChange={onChange} /></div>;
    default: return <input {...common} maxLength={f.max || 200} />;
  }
}

function Editor({ cfg, item, onClose, refs }) {
  const [data, setData] = useState(() => item || Object.fromEntries(cfg.fields.map((f) => [f.name, f.default ?? (f.type === "checkbox" ? false : f.type === "multi" || f.type === "faqs" ? [] : f.type === "hours" ? {} : f.type === "number" || f.type === "money" ? null : "")])));
  const save = async (e) => {
    e.preventDefault();
    const body = Object.fromEntries(cfg.fields.map((f) => [f.name, data[f.name]]));
    try {
      if (item) await api.put(`/admin/${cfg.entity}/${item.id}`, body); else await api.post(`/admin/${cfg.entity}`, body);
      toast.success("Salvato"); onClose(true);
    } catch (err) { toast.error(errorMessage(err)); }
  };
  return (
    <form onSubmit={save} className="card-soft grid gap-5 p-6" data-testid="crud-editor">
      <h2 className="text-2xl">{item ? "Modifica" : "Nuovo"}</h2>
      {item?.review_status && <p className={`w-fit rounded-full px-3 py-1 text-xs ${item.review_status === "approved" ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-900"}`} data-testid="review-badge">Revisione medica: {item.review_status === "approved" ? "approvata" : "in attesa"}</p>}
      <div className="grid gap-5 md:grid-cols-2">
        {cfg.fields.map((f) => (
          <label key={f.name} className={`text-sm ${["textarea", "html", "faqs", "hours", "multi", "media"].includes(f.type) ? "md:col-span-2" : ""}`}>{f.label}{f.required && " *"}
            <Field f={f} value={data[f.name]} onChange={(v) => setData({ ...data, [f.name]: v })} refs={refs} />
          </label>
        ))}
      </div>
      <div className="flex gap-3"><button className="btn-primary" data-testid="crud-save">Salva</button><button type="button" className="btn-outline" onClick={() => onClose(false)} data-testid="crud-cancel">Chiudi</button></div>
    </form>
  );
}

export default function CrudPage({ cfg }) {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(undefined);
  const [filter, setFilter] = useState("");
  const refs = useRefs(cfg.fields);
  const write = can(cfg.write);
  const load = () => api.get(`/admin/${cfg.entity}`, { params: filter ? { status: filter } : {} }).then((r) => setItems(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.entity, filter]);
  const run = async (fn, okMsg) => { try { await fn(); if (okMsg) toast.success(okMsg); load(); } catch (e) { toast.error(errorMessage(e)); } };
  const move = (i, d) => { const ids = items.map((x) => x.id); [ids[i], ids[i + d]] = [ids[i + d], ids[i]]; run(() => api.post(`/admin/${cfg.entity}/reorder`, { ids })); };
  if (editing !== undefined) return <Editor cfg={cfg} item={editing} refs={refs} onClose={(saved) => { setEditing(undefined); if (saved) load(); }} />;
  return (
    <>
      <AdminTitle title={cfg.title}>
        {cfg.statuses && <select className="field !w-auto !py-2 text-sm" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="crud-status-filter"><option value="">Tutti gli stati</option>{Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>}
        {write && <button className="btn-primary !py-2 text-sm" onClick={() => setEditing(null)} data-testid="crud-new"><Plus size={16} /> Nuovo</button>}
      </AdminTitle>
      {cfg.help && <p className="mb-6 max-w-3xl text-sm text-[var(--muted-ink)]">{cfg.help}</p>}
      <div className="card-soft overflow-x-auto">
        <table className="w-full text-sm" data-testid="crud-table">
          <thead className="bg-[var(--ivory-soft)] text-left text-xs uppercase tracking-wider text-[var(--muted-ink)]"><tr><th className="p-3">Ordine</th>{cfg.columns.map(([k, l]) => <th key={k} className="p-3">{l}</th>)}<th className="p-3">Azioni</th></tr></thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={it.id} className="border-t border-[var(--line)]" data-testid={`crud-row-${it.id}`}>
                <td className="p-3">{write && <span className="flex gap-1"><button disabled={i === 0} onClick={() => move(i, -1)} aria-label="Sposta su" data-testid={`crud-up-${i}`}><ArrowUp size={14} /></button><button disabled={i === items.length - 1} onClick={() => move(i, 1)} aria-label="Sposta giù"><ArrowDown size={14} /></button></span>}</td>
                {cfg.columns.map(([k]) => <td key={k} className="p-3">{k === "status" ? STATUS_LABEL[it.status] : k === "review_status" ? (it.review_status === "approved" ? "✓ revisionato" : "da revisionare") : typeof it[k] === "boolean" ? (it[k] ? "Sì" : "No") : cfg.format?.[k] ? cfg.format[k](it[k], it) : String(it[k] ?? "")}</td>)}
                <td className="p-3"><div className="flex flex-wrap gap-2">
                  {write && <button className="text-[var(--petrol)] underline" onClick={() => api.get(`/admin/${cfg.entity}/${it.id}`).then((r) => setEditing(r.data))} data-testid={`crud-edit-${it.id}`}>Modifica</button>}
                  {write && cfg.statuses && it.status !== "published" && <button className="underline" onClick={() => run(() => api.post(`/admin/${cfg.entity}/${it.id}/status`, { status: "published" }), "Pubblicato")} data-testid={`crud-publish-${it.id}`}>Pubblica</button>}
                  {write && cfg.statuses && it.status === "published" && <button className="underline" onClick={() => run(() => api.post(`/admin/${cfg.entity}/${it.id}/status`, { status: "hidden" }), "Nascosto")} data-testid={`crud-hide-${it.id}`}>Nascondi</button>}
                  {can("content.review") && it.review_status === "pending" && <button className="text-emerald-700 underline" onClick={() => run(() => api.post(`/admin/${cfg.entity}/${it.id}/review`), "Revisione approvata")} data-testid={`crud-review-${it.id}`}>Approva revisione</button>}
                  {write && it.status !== "archived" && <button className="text-red-700 underline" onClick={() => window.confirm("Confermi?") && run(() => api.delete(`/admin/${cfg.entity}/${it.id}`), "Fatto")} data-testid={`crud-delete-${it.id}`}>{cfg.statuses ? "Archivia" : cfg.entity === "staff" || cfg.entity === "resources" ? "Disattiva" : "Elimina"}</button>}
                </div></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-[var(--muted-ink)]">Nessun elemento.</td></tr>}
          </tbody>
        </table>
      </div>
    </>
  );
}
